import { ChangeDetectionStrategy, Component, computed, inject, input, InputSignal, Signal } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { MatSelectChange } from '@angular/material/select';
import { ColorEvent } from 'ngx-color';
import { ColorSketchModule } from 'ngx-color/sketch';

import { GeometryFormGroup } from '@connect-services/client/components/project-settings/symbology-edit/symbology-edit.component';
import { SpriteInformation } from '@connect-services/client/services/legend.service';
import {
    DEFAULT_COLOR,
    NO_SYMBOL,
    SymbologyEditService,
    SymbologyStyleScope,
} from '@connect-services/client/services/symbology-edit.service';
import CS_default_sprite from '@connect-services/src/assets/sprites/CS_default_sprite.json';
import { MapboxStyle } from '@connect-services/src/sdk/api';
import { RessourcesModule } from '@ressources/client/ressources.module';
import { FormService } from '@shared/client/services/form.service';

type SymbologyDisplayConfig = {
    hasColor: boolean;
    hasOpacity: boolean;
    hasSize: boolean;
    hasSymbol: boolean;
    hasSymbolOpacity: boolean;
    hasZooms: boolean;
};

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [ColorSketchModule, RessourcesModule],
    selector: 'app-symbology-base-form',
    styleUrl: '../../edit-deposit-layer/edit-deposit-layer.component.scss',
    templateUrl: './symbology-base-form.component.html',
})
export class SymbologyBaseFormComponent {
    public readonly formGroup: InputSignal<FormGroup<GeometryFormGroup>> = input.required();
    public readonly geometry = input.required<MapboxStyle.TypeEnum>();
    public readonly section = input.required<SymbologyStyleScope>();

    protected readonly color = computed(() => {
        switch (this.geometry()) {
            case MapboxStyle.TypeEnum.SYMBOL:
                return this.symbologyEditService.pointColor();

            case MapboxStyle.TypeEnum.LINE:
                return this.symbologyEditService.lineColor();

            case MapboxStyle.TypeEnum.FILL:
                return this.symbologyEditService.polygonColor();
            default:
                return DEFAULT_COLOR;
        }
    });
    protected readonly displayStyle = computed<SymbologyDisplayConfig>(() => {
        const isCommon = this.section() === SymbologyStyleScope.COMMON;
        const isSpecific = this.section() === SymbologyStyleScope.SPECIFIC;
        const isPoint = this.geometry() === MapboxStyle.TypeEnum.SYMBOL;
        const isPolygon = this.geometry() === MapboxStyle.TypeEnum.FILL;

        return {
            hasColor: isCommon,
            hasOpacity: (isCommon && isPolygon) || (isSpecific && !isPolygon),
            hasSize: isCommon && !isPolygon,
            hasSymbol: isCommon && isPoint,
            hasSymbolOpacity: isSpecific && isPolygon,
            hasZooms: isSpecific,
        };
    });
    protected readonly formService = inject(FormService);
    protected readonly MapboxStyle = MapboxStyle;
    protected readonly sprites: Signal<{ name: string; style: { [key: string]: string } }[]> = computed(() => {
        const sprites = Object.entries(this.SPRITES_DATA).map(([name, sprite]) => ({
            name,
            style: {
                'background-image': 'url(assets/sprites/CS_default_sprite.png)',
                'background-position': `-${sprite.x}px -${sprite.y}px`,
                'background-repeat': 'no-repeat',
                display: 'inline-block',
                height: `${sprite.height}px`,
                'image-rendering': 'pixelated',
                width: `${sprite.width}px`,
            },
        }));

        if (this.geometry() === MapboxStyle.TypeEnum.SYMBOL) {
            return sprites;
        }

        return [{ name: NO_SYMBOL, style: {} }, ...sprites];
    });
    protected readonly SPRITES_DATA: Record<string, SpriteInformation> = CS_default_sprite;

    protected readonly symbologyEditService = inject(SymbologyEditService);

    protected onChangeColor(event: ColorEvent): void {
        switch (this.geometry()) {
            case MapboxStyle.TypeEnum.LINE:
                this.symbologyEditService.onChangeLineColor(event);
                break;
            case MapboxStyle.TypeEnum.FILL:
                this.symbologyEditService.onChangePolygonColor(event);
                break;
            case MapboxStyle.TypeEnum.SYMBOL:
                this.symbologyEditService.onChangePointColor(event);
                break;
        }
    }

    protected onChangeSymbol(event: MatSelectChange): void {
        if (this.geometry() !== MapboxStyle.TypeEnum.FILL) {
            return;
        }

        this.symbologyEditService.onChangePolygonSymbol(event);
    }
}
